// A test run's wire schemas: its kind, each question's result, and a
// golden run's comparison with the active toolset's. Part of the Curator API
// contract, so it imports only effect. The Exchange runs and compares test
// runs in src/exchange/test-runs.ts. See Design > Exchange, "Test runs".

import { Schema } from "effect";

export const TestRunKind = Schema.Literals(["examples", "golden"]);
export type TestRunKind = typeof TestRunKind.Type;

// One question's result, as `test_runs.results` stores it, in a JSON array
// that the HubStore decodes with this schema. `stage` is a HubError's stage.
// `requestId` is the ID the question was answered under, which its events and
// log lines carry. A row recorded before either existed has neither, and a
// question that failed after its retries has no request ID. See Design >
// Observability, "Request ID".
export const TestResult = Schema.Struct({
  question: Schema.String, expectIntent: Schema.NullOr(Schema.String), expectOutcome: Schema.String,
  outcome: Schema.String, intent: Schema.NullOr(Schema.String), ms: Schema.Finite, note: Schema.String, pass: Schema.Boolean,
  stage: Schema.optionalKey(Schema.String), requestId: Schema.optionalKey(Schema.String),
});
export type TestResult = typeof TestResult.Type;

// One run's result for a question, as a comparison shows it.
const ComparedResult = Schema.Struct({ outcome: Schema.String, intent: Schema.NullOr(Schema.String), pass: Schema.Boolean });
// A question of a comparison, with this run's result and the earlier run's:
// null for the run that didn't ask it.
const ComparedQuestion = Schema.Struct({ question: Schema.String, run: Schema.NullOr(ComparedResult), earlier: Schema.NullOr(ComparedResult) });
const ComparedLists = {
  newlyMissing: Schema.Array(ComparedQuestion), newlyPassing: Schema.Array(ComparedQuestion),
  missingInBoth: Schema.Array(ComparedQuestion), askedByOne: Schema.Array(ComparedQuestion),
};
export type ComparedLists = { [K in keyof typeof ComparedLists]: (typeof ComparedQuestion.Type)[] };

// A golden run of a toolset that isn't active, compared with the active
// toolset's latest golden run that ended passed or failed: the earlier run,
// and the questions that miss now and passed then, that pass now and missed
// then, that miss in both, and that only one of the two runs asked. Or why
// there's nothing to compare. Advice, not an activation check. See Design >
// Exchange, "Test runs of a toolset".
export const GoldenComparison = Schema.Union([
  Schema.TaggedStruct("Compared", {
    earlier: Schema.Struct({ runId: Schema.String, commit: Schema.String, finishedAt: Schema.NullOr(Schema.Int) }),
    ...ComparedLists,
  }),
  Schema.TaggedStruct("NotCompared", {
    reason: Schema.Literals(["running", "error", "active", "noActiveToolset", "noActiveGoldenRun"]),
    detail: Schema.String,
  }),
]).annotate({ identifier: "GoldenComparison" });
export type GoldenComparison = typeof GoldenComparison.Type;

// The counts of each list of a toolset's comparison, as its ToolsetSummary
// carries them, with the two runs compared.
export const GoldenComparisonCounts = Schema.Struct({
  runId: Schema.String, earlierRunId: Schema.String,
  newlyMissing: Schema.Int, newlyPassing: Schema.Int, missingInBoth: Schema.Int, askedByOne: Schema.Int,
}).annotate({ identifier: "GoldenComparisonCounts" });
export type GoldenComparisonCounts = typeof GoldenComparisonCounts.Type;
